import type {ReactNode} from 'react';
import clsx from 'clsx';
import Link from 'next/link';

import {ContentBlockHeading} from '@/modules/foundation/components/content-block-heading';
import {GridList} from '@/modules/foundation/components/grid-list';
import {ArrowRightIcon} from '@/modules/foundation/components/icons/arrow-right-icon';
import {Heading} from '@/modules/foundation/components/typography/heading';
import type {PortableTextOverrides} from '@/modules/foundation/sanity/portable-text';
import type {ContentBlockContainerProps} from '@/modules/page/components/content-blocks/block-container';
import {ContentBlockContainer} from '@/modules/page/components/content-blocks/block-container';
import type {FinknIcons} from '@/shared/renderIcon';
import {HoverIcon} from '@/shared/renderIcon';

interface Props extends ContentBlockContainerProps {
	heading?: string;
	description?: ReactNode;
	listItems?: {
		_key: string;
		title: string;
		href: string;
		icon?: FinknIcons;
	}[];
	anchorHeadings?: boolean;
	insideToc?: boolean;
}

export const descriptionPortableTextOverrides: PortableTextOverrides = {
	p: 'text-xl',
};

export const AreaList = ({
	heading,
	description,
	listItems,
	options,
	anchorHeadings,
	insideToc,
}: Props) => {
	return (
		<ContentBlockContainer options={options} insideToc={insideToc}>
			<div className="@container">
				<ContentBlockHeading heading={heading} textCenter anchorHeading={anchorHeadings}>
					{description}
				</ContentBlockHeading>

				<GridList>
					{listItems?.length &&
						listItems.map((item) => (
							<ListItem
								key={item._key}
								title={item.title}
								href={item.href}
								icon={item.icon}
								headingLevel={heading ? 3 : 2}
							/>
						))}
				</GridList>
			</div>
		</ContentBlockContainer>
	);
};

type ListItemProps = {
	href: string;
	title: string;
	icon?: FinknIcons;
	headingLevel?: 2 | 3;
};

const ListItem = ({href, title, icon, headingLevel}: ListItemProps) => {
	return (
		<li>
			<Link
				href={href}
				className={clsx(
					'block',
					'bg-white',
					'shadow-card',
					'p-6',
					'group',
					'rounded-[3px]',
				)}
			>
				<HoverIcon icon={icon} className="mb-8" />
				<Heading
					level={headingLevel ?? 2}
					size="sm"
					asChild
					className={clsx(
						'flex',
						'items-center',
						'justify-between',
						'gap-4',
						'transition-colors',
						'group-hover:text-primary-15',
					)}
				>
					<span>
						{title}
						<ArrowRightIcon
							aria-hidden
							className={clsx(
								'text-grey-25',
								'text-base',
								'shrink-0',
								'transition-all',
								'group-hover:text-primary-15',
								'mr-2',
								'group-hover:mr-0',
							)}
						/>
					</span>
				</Heading>
			</Link>
		</li>
	);
};
